import axios from 'axios';

const API_URL = import.meta.env.VITE_API_URL;

// Create axios instance with default config
const api = axios.create({
  baseURL: API_URL,
  headers: {
    'Content-Type': 'application/json',
  },
  withCredentials: true,
});

// Add token to requests if available
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('token');
    if (token) {
      config.headers['Authorization'] = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Handle response errors
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response && error.response.status === 401) {
      // Only clear token, do not redirect globally
      localStorage.removeItem('token');
      // window.location.href = '/login'; // REMOVE THIS LINE
    }
    return Promise.reject(error);
  }
);

// Auth service methods
const authService = {
  // Login user
  login: async (email, password) => {
    try {
      const response = await api.post('/auth/login', { email, password });
      return response.data;
    } catch (error) {
      if (error.response) {
        const status = error.response.status;
        const backendMessage = error.response.data?.error;
        const errorCode = error.response.data?.code;
        
        // Handle rate limit errors (429)
        if (status === 429) {
          if (errorCode === 'AUTH_RATE_LIMIT_EXCEEDED') {
            throw new Error('Too many login attempts. Please wait 5 minutes and try again');
          } else if (errorCode === 'RATE_LIMIT_EXCEEDED') {
            throw new Error('Too many requests. Please wait a few minutes and try again');
          } else {
            throw new Error(backendMessage || 'Too many attempts. Please try again later');
          }
        }
        
        // Handle other error statuses
        if (status === 401) {
          throw new Error(backendMessage || 'Invalid credentials. Please check your email and password');
        }
        
        if (status === 400) {
          throw new Error(backendMessage || 'Invalid request. Please check your information');
        }
        
        if (status === 403) {
          throw new Error(backendMessage || 'Access denied. Your account may be suspended');
        }
        
        if (status >= 500) {
          throw new Error('Server error. Please try again later');
        }
        
        // Pass through the backend error message for better UX
        if (backendMessage) {
          throw new Error(backendMessage);
        }
      }
      
      // Handle network errors
      if (error.code === 'NETWORK_ERROR' || error.code === 'ERR_NETWORK' || !error.response) {
        throw new Error('Connection problem. Please check your internet and try again');
      }
      
      throw new Error(error.message || 'Login failed');
    }
  },

  // Register user (legacy — still works but bypasses verification)
  register: async (userData) => {
    try {
      const response = await api.post('/auth/register', userData);
      return response.data;
    } catch (error) {
      if (error.response) {
        const status = error.response.status;
        const backendMessage = error.response.data?.error;
        const errorCode = error.response.data?.code;
        
        // Handle rate limit errors (429)
        if (status === 429) {
          if (errorCode === 'AUTH_RATE_LIMIT_EXCEEDED') {
            throw new Error('Too many registration attempts. Please wait 5 minutes and try again');
          } else {
            throw new Error(backendMessage || 'Too many attempts. Please try again later');
          }
        }
        
        // Handle other error statuses
        if (status === 400) {
          throw new Error(backendMessage || 'Invalid registration data. Please check your information');
        }
        
        if (status >= 500) {
          throw new Error('Server error. Please try again later');
        }
        
        // Pass through the backend error message for better UX
        if (backendMessage) {
          throw new Error(backendMessage);
        }
      }
      
      // Handle network errors
      if (error.code === 'NETWORK_ERROR' || error.code === 'ERR_NETWORK' || !error.response) {
        throw new Error('Connection problem. Please check your internet and try again');
      }
      
      throw new Error(error.message || 'Registration failed');
    }
  },

  // Step 1: Send verification code to email
  sendVerificationCode: async (userData) => {
    try {
      const response = await api.post('/auth/send-verification', userData);
      return response.data;
    } catch (error) {
      if (error.response) {
        const status = error.response.status;
        const backendMessage = error.response.data?.error;
        if (status === 429) {
          throw new Error('Too many attempts. Please wait a few minutes and try again');
        }
        if (backendMessage) throw new Error(backendMessage);
      }
      if (!error.response) throw new Error('Connection problem. Please check your internet');
      throw new Error(error.message || 'Failed to send verification code');
    }
  },

  // Step 2: Verify code and complete registration
  verifyAndRegister: async ({ email, code }) => {
    try {
      const response = await api.post('/auth/verify-and-register', { email, code });
      return response.data;
    } catch (error) {
      if (error.response) {
        const backendMessage = error.response.data?.error;
        if (backendMessage) throw new Error(backendMessage);
      }
      if (!error.response) throw new Error('Connection problem. Please check your internet');
      throw new Error(error.message || 'Verification failed');
    }
  },

  // Resend verification code
  resendVerificationCode: async (email) => {
    try {
      const response = await api.post('/auth/resend-verification', { email });
      return response.data;
    } catch (error) {
      if (error.response) {
        const backendMessage = error.response.data?.error;
        if (backendMessage) throw new Error(backendMessage);
      }
      throw new Error('Failed to resend verification code');
    }
  },

  // Send verification code for organization registration
  sendOrgVerificationCode: async (orgData) => {
    try {
      const response = await api.post('/organizations/send-verification', orgData);
      return response.data;
    } catch (error) {
      if (error.response) {
        const backendMessage = error.response.data?.message;
        if (backendMessage) throw new Error(backendMessage);
      }
      throw new Error('Failed to send verification code');
    }
  },

  // Verify code and complete org registration
  verifyAndRegisterOrg: async ({ email, code }) => {
    try {
      const response = await api.post('/organizations/verify-and-register', { email, code });
      return response.data;
    } catch (error) {
      if (error.response) {
        const backendMessage = error.response.data?.message;
        if (backendMessage) throw new Error(backendMessage);
      }
      throw new Error('Organization verification failed');
    }
  },

  // Send verification code for office registration
  sendOfficeVerificationCode: async (companyId, officeData) => {
    try {
      const response = await api.post(`/organizations/${companyId}/offices/send-verification`, officeData);
      return response.data;
    } catch (error) {
      if (error.response) {
        const backendMessage = error.response.data?.message;
        if (backendMessage) throw new Error(backendMessage);
      }
      throw new Error('Failed to send verification code');
    }
  },

  // Verify code and complete office registration
  verifyAndRegisterOffice: async (companyId, { email, code }) => {
    try {
      const response = await api.post(`/organizations/${companyId}/offices/verify-and-register`, { email, code });
      return response.data;
    } catch (error) {
      if (error.response) {
        const backendMessage = error.response.data?.message;
        if (backendMessage) throw new Error(backendMessage);
      }
      throw new Error('Office verification failed');
    }
  },

  // Get current user
  getCurrentUser: async () => {
    try {
      const response = await api.get('/auth/me');
      // Return full response including accountStatus if present
      return response.data;
    } catch (error) {
      // Re-throw with response data for handling in AuthContext
      const err = new Error(error.response?.data?.error || 'Failed to get user data');
      err.response = error.response;
      throw err;
    }
  },

  // Update profile
  updateProfile: async (userData) => {
    try {
      const response = await api.put('/auth/profile', userData);
      return response.data.data;
    } catch (error) {
      throw new Error(error.response?.data?.error || 'Profile update failed');
    }
  },

  // Delete account
  deleteAccount: async (password) => {
    try {
      const response = await api.delete('/auth/account', {
        data: { password }
      });
      return response.data;
    } catch (error) {
      throw new Error(error.response?.data?.error || 'Account deletion failed');
    }
  },

  // Forgot password
  forgotPassword: async (email) => {
    try {
      const response = await api.post('/auth/forgotpassword', { email });
      return response.data;
    } catch (error) {
      if (error.response) {
        const status = error.response.status;
        const backendMessage = error.response.data?.error;
        const errorCode = error.response.data?.code;
        
        // Handle rate limit errors (429)
        if (status === 429) {
          if (errorCode === 'AUTH_RATE_LIMIT_EXCEEDED') {
            throw new Error('Too many password reset attempts. Please wait 5 minutes and try again');
          } else {
            throw new Error(backendMessage || 'Too many attempts. Please try again later');
          }
        }
        
        // Handle other errors
        if (status === 404) {
          throw new Error(backendMessage || 'No account found with this email address');
        }
        
        if (status >= 500) {
          throw new Error('Server error. Please try again later');
        }
        
        if (backendMessage) {
          throw new Error(backendMessage);
        }
      }
      
      // Handle network errors
      if (error.code === 'NETWORK_ERROR' || error.code === 'ERR_NETWORK' || !error.response) {
        throw new Error('Connection problem. Please check your internet and try again');
      }
      
      throw new Error(error.response?.data?.error || 'Failed to process request');
    }
  },

  // Social login
  socialLogin: async (provider, token) => {
    try {
      // In a real implementation, this would call a backend endpoint
      // For this example, we're simulating the response
      
      // Mock response for development purposes
      const mockResponse = {
        success: true,
        token: `mock-token-${Date.now()}`,
        user: {
          id: `user-${Date.now()}`,
          name: `${provider.charAt(0).toUpperCase() + provider.slice(1)} User`,
          email: `${provider}-user@example.com`,
          role: 'team-member',
          createdAt: new Date().toISOString()
        }
      };
      
      // Simulate API delay
      await new Promise(resolve => setTimeout(resolve, 500));
      
      return mockResponse;
      
      // Real implementation would be something like:
      // const response = await api.post('/auth/social-login', { provider, token });
      // return response.data;
    } catch (error) {
      throw new Error(error.response?.data?.error || `${provider} login failed`);
    }
  },

  // Reset password
  resetPassword: async (token, password) => {
    try {
      const response = await api.put(`/auth/resetpassword/${token}`, { password });
      return response.data;
    } catch (error) {
      if (error.response) {
        const status = error.response.status;
        const backendMessage = error.response.data?.error;
        const errorCode = error.response.data?.code;
        
        // Handle rate limit errors (429)
        if (status === 429) {
          if (errorCode === 'AUTH_RATE_LIMIT_EXCEEDED') {
            throw new Error('Too many password reset attempts. Please wait 5 minutes and try again');
          } else {
            throw new Error(backendMessage || 'Too many attempts. Please try again later');
          }
        }
        
        // Handle other errors
        if (status === 400 || status === 401) {
          throw new Error(backendMessage || 'Invalid or expired reset link. Please request a new one');
        }
        
        if (status >= 500) {
          throw new Error('Server error. Please try again later');
        }
        
        if (backendMessage) {
          throw new Error(backendMessage);
        }
      }
      
      // Handle network errors
      if (error.code === 'NETWORK_ERROR' || error.code === 'ERR_NETWORK' || !error.response) {
        throw new Error('Connection problem. Please check your internet and try again');
      }
      
      throw new Error(error.response?.data?.error || 'Failed to reset password');
    }
  },

  // Export user data
  exportUserData: async () => {
    try {
      const response = await api.get('/auth/export-data', {
        responseType: 'blob'
      });
      return response.data;
    } catch (error) {
      throw new Error(error.response?.data?.error || 'Failed to export data');
    }
  },

  // Self-deactivate account
  selfDeactivate: async (password, reason = '') => {
    try {
      const response = await api.put('/auth/self-deactivate', { password, reason });
      return response.data;
    } catch (error) {
      throw new Error(error.response?.data?.error || 'Failed to deactivate account');
    }
  },

  // Request reactivation email
  requestReactivation: async () => {
    try {
      const response = await api.post('/auth/request-reactivation');
      return response.data;
    } catch (error) {
      const data = error.response?.data;
      // Include supportRequired flag in error
      const err = new Error(data?.error || 'Failed to request reactivation');
      err.supportRequired = data?.supportRequired;
      throw err;
    }
  },

  // Reactivate account with token
  reactivateWithToken: async (token) => {
    try {
      const response = await api.put(`/auth/reactivate/${token}`);
      return response.data;
    } catch (error) {
      throw new Error(error.response?.data?.error || 'Failed to reactivate account');
    }
  },
};

export default authService;

// Export the axios instance for use in other services
export { api };
